import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isToday, parseISO, differenceInMinutes } from 'date-fns';
import { ExclamationTriangleIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/solid';

interface Violation {
  id: string;
  threshold_name: string;
  started_at: string;
  peak_level: number;
  resolved_at?: string;
}

interface ViolationTimelineProps {
  violations: Violation[];
  timeRange?: number; // Time range in hours (default: 24)
}

const ViolationTimeline: React.FC<ViolationTimelineProps> = ({
  violations,
  timeRange = 24,
}) => {
  const [selectedViolation, setSelectedViolation] = useState<Violation | null>(null);
  const [hoveredViolation, setHoveredViolation] = useState<string | null>(null);

  // Get current time and time range
  const now = new Date();
  const startTime = new Date(now.getTime() - timeRange * 60 * 60 * 1000);

  // Filter violations within time range
  const timelineViolations = violations.filter((v) => {
    const violationTime = parseISO(v.started_at);
    return violationTime >= startTime && violationTime <= now;
  });

  // Calculate position on timeline (0-100%)
  const getPositionPercent = (timestamp: string): number => {
    const time = parseISO(timestamp);
    const elapsed = time.getTime() - startTime.getTime();
    const total = now.getTime() - startTime.getTime();
    return Math.max(0, Math.min(100, (elapsed / total) * 100));
  };

  // Get severity color
  const getSeverityColor = (peakLevel: number): string => {
    if (peakLevel >= 100) return '#EF4444'; // Red
    if (peakLevel >= 95) return '#F97316';  // Orange
    if (peakLevel >= 85) return '#F59E0B';  // Yellow
    return '#10B981'; // Green
  };

  // Generate time markers (every 4 hours)
  const timeMarkers: Date[] = [];
  for (let i = 0; i <= timeRange; i += 4) {
    timeMarkers.push(new Date(startTime.getTime() + i * 60 * 60 * 1000));
  }

  // Check if there are active violations
  const activeViolations = violations.filter((v) => !v.resolved_at);
  const hasActiveViolations = activeViolations.length > 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ClockIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Violation Timeline
          </h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Last {timeRange} hours
          </span>
        </div>

        {/* Status indicator */}
        <div className="flex items-center gap-2">
          {hasActiveViolations ? (
            <>
              <motion.div
                className="w-3 h-3 rounded-full bg-red-500"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [1, 0.6, 1],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 2,
                }}
              />
              <span className="text-sm font-medium text-red-600 dark:text-red-400">
                {activeViolations.length} Active
              </span>
            </>
          ) : (
            <>
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                All Clear
              </span>
            </>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Time axis */}
        <div className="relative h-20 bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
          {/* Time markers */}
          <div className="absolute inset-0 flex justify-between items-end pb-2 px-2">
            {timeMarkers.map((time, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mb-1" />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {format(time, 'HH:mm')}
                </span>
              </div>
            ))}
          </div>

          {/* Violation markers */}
          <AnimatePresence>
            {timelineViolations.map((violation) => {
              const position = getPositionPercent(violation.started_at);
              const color = getSeverityColor(violation.peak_level);
              const isHovered = hoveredViolation === violation.id;
              const isSelected = selectedViolation?.id === violation.id;
              const isActive = !violation.resolved_at;

              return (
                <motion.div
                  key={violation.id}
                  className="absolute top-4 cursor-pointer"
                  style={{ left: `${position}%` }}
                  initial={{ scale: 0, y: 10 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0, y: 10 }}
                  whileHover={{ scale: 1.4, zIndex: 10 }}
                  onHoverStart={() => setHoveredViolation(violation.id)}
                  onHoverEnd={() => setHoveredViolation(null)}
                  onClick={() => setSelectedViolation(violation)}
                >
                  {/* Marker */}
                  <motion.div
                    className="relative"
                    animate={isActive ? {
                      scale: [1, 1.2, 1],
                    } : {}}
                    transition={{
                      repeat: isActive ? Infinity : 0,
                      duration: 2,
                    }}
                  >
                    <div
                      className="w-4 h-4 rounded-full border-2 border-white dark:border-gray-800 shadow-lg"
                      style={{ backgroundColor: color }}
                    />
                    {isActive && (
                      <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{ backgroundColor: color, opacity: 0.3 }}
                        animate={{ scale: [1, 2, 1], opacity: [0.3, 0, 0.3] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                      />
                    )}
                  </motion.div>

                  {/* Hover tooltip */}
                  <AnimatePresence>
                    {(isHovered || isSelected) && (
                      <motion.div
                        className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg shadow-xl p-3 w-48 z-20"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <div className="font-semibold mb-1">
                          {violation.threshold_name || 'Sound Level Exceeded'}
                        </div>
                        <div className="space-y-1 text-gray-300 dark:text-gray-600">
                          <div>Peak: {violation.peak_level.toFixed(1)} dB</div>
                          <div>Time: {format(parseISO(violation.started_at), 'HH:mm:ss')}</div>
                          {violation.resolved_at ? (
                            <div className="text-green-400 dark:text-green-600 font-medium">
                              Resolved {differenceInMinutes(parseISO(violation.resolved_at), parseISO(violation.started_at))} min later
                            </div>
                          ) : (
                            <div className="text-red-400 dark:text-red-600 font-medium flex items-center gap-1">
                              <ExclamationTriangleIcon className="h-3 w-3" />
                              Active
                            </div>
                          )}
                        </div>
                        {/* Arrow */}
                        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-100 rotate-45" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* "Now" indicator */}
          <motion.div
            className="absolute top-0 bottom-0 right-0 w-px bg-blue-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <div className="absolute -top-1 -left-1 w-2 h-2 bg-blue-500 rounded-full" />
            <span className="absolute -top-6 -right-6 text-xs text-blue-600 dark:text-blue-400 font-medium">
              Now
            </span>
          </motion.div>
        </div>

        {/* Summary */}
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-gray-600 dark:text-gray-400">Critical (≥100 dB)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span className="text-gray-600 dark:text-gray-400">High (95-100 dB)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-gray-600 dark:text-gray-400">Warning (85-95 dB)</span>
            </div>
          </div>
          <div className="text-gray-500 dark:text-gray-400">
            {timelineViolations.length} violation{timelineViolations.length !== 1 ? 's' : ''} in period
          </div>
        </div>
      </div>

      {/* Empty state */}
      {timelineViolations.length === 0 && (
        <div className="mt-8 text-center py-8">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
            No violations in the last {timeRange} hours
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
            Sound levels are within acceptable limits
          </p>
        </div>
      )}
    </div>
  );
};

export default ViolationTimeline;
